import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllPackagesWithSlots,
  updatePackageSlot,
} from "../../../services/packageService";
import Toster from "../../common/Toster";
import Pagination from "../../common/Pagination";
import { AdminPageLayout, AdminPageHeader, AdminPageCard } from "../../common/AdminPageLayout";
import { SUPPLIER_PAGE_META } from "../../common/supplierPageMeta";
import { paginateClientList } from "../../../utils/clientPagination";
import { formatListingDate } from "../../../utils/formatListingDate";

const CalendarIcon = () => (
  <svg
    className="w-4 h-4 text-gray-500"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);

const Slots = () => {
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [changedPackages, setChangedPackages] = useState({});
  const [showCalendarSheet, setShowCalendarSheet] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkSlotValue, setBulkSlotValue] = useState("");
  const [showLowSlotUpdate, setShowLowSlotUpdate] = useState(false);
  const [selectedPackagesForBulk, setSelectedPackagesForBulk] = useState(
    new Set()
  );
  const [validationErrors, setValidationErrors] = useState({});
  const [lowAvailabilityWarnings, setLowAvailabilityWarnings] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("all"); // all, low, medium, high, full
  const [showToster, setShowToster] = useState(false);
  const [toasterMessage, setToasterMessage] = useState("");
  const [toasterType, setToasterType] = useState("success");
  const [originalSlotValues, setOriginalSlotValues] = useState({}); // Track original values before changes
  const [inputValues, setInputValues] = useState({}); // Track input values separately for better typing UX
  const [availableInputValues, setAvailableInputValues] = useState({}); // Track available slot input values
  const [currentPage, setCurrentPage] = useState(1);
  const queryClient = useQueryClient();

  // Show toast notification
  const showToast = (message, type = "success") => {
    setToasterMessage(message);
    setToasterType(type);
    setShowToster(true);
  };

  // Format date to YYYY-MM-DD string
  const formatDateToString = (date) => {
    if (typeof date === "string" && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return date;
    }
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }
    return new Date(date).toISOString().split("T")[0];
  };

  // Fetch packages with slots
  const dateStr = formatDateToString(selectedDate);
  const {
    data: packagesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["package-slots", dateStr],
    queryFn: () => getAllPackagesWithSlots(dateStr),
  });

  // Update slot mutation
  const updateSlotMutation = useMutation({
    mutationFn: ({ packageId, date, slot }) =>
      updatePackageSlot(packageId, date, slot),
    onSuccess: () => {
      queryClient.invalidateQueries(["package-slots"]);
      refetch();
    },
  });

  // Clear original values and changed packages when date changes
  useEffect(() => {
    setOriginalSlotValues({});
    setChangedPackages({});
    setInputValues({});
    setAvailableInputValues({});
  }, [selectedDate]);

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async (updates) => {
      const promises = updates.map(({ packageId, date, slot }) =>
        updatePackageSlot(packageId, date, slot)
      );
      return Promise.all(promises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["package-slots"]);
      refetch();
      setSelectedPackagesForBulk(new Set());
      setBulkSlotValue("");
      setShowBulkActions(false);
    },
  });

  const packages = packagesData?.data || [];

  // Helper functions for slots
  const getSlots = (pkg, date) => {
    const dateStr = formatDateToString(date);
    return pkg.slots?.[dateStr] ?? pkg.defaultSlot ?? 0;
  };

  const getBookedSlots = (pkg, date) => {
    const dateStr = formatDateToString(date);
    return pkg.bookedSlots?.[dateStr] ?? 0;
  };

  // Get available slots from backend-calculated value
  // All business logic is handled on backend - frontend only consumes and displays
  const getAvailableSlots = (pkg, date) => {
    const dateStr = formatDateToString(date);
    // Use backend-calculated available slots if available
    if (pkg.availableSlots && pkg.availableSlots[dateStr] !== undefined) {
      return pkg.availableSlots[dateStr];
    }
    // Fallback: calculate from slots and booked (should not happen if backend is correct)
    const slots = getSlots(pkg, date);
    const booked = getBookedSlots(pkg, date);
    return Math.max(0, slots - booked);
  };

  // Filter packages to show only those with rates for the selected date
  const filteredPackages = packages
    .map((trip) => ({
      ...trip,
      packages: trip.packages.filter(
        (pkg) => pkg.hasRateForDate && !pkg.isStopSale
      ),
    }))
    .filter((trip) => trip.packages.length > 0);

  // Calculate statistics using backend-calculated values
  // All business logic is handled on backend - frontend only consumes and displays
  const calculateStatistics = () => {
    let totalSlots = 0;
    let totalBooked = 0;
    let totalAvailable = 0;
    let lowAvailabilityCount = 0;

    filteredPackages.forEach((trip) => {
      trip.packages.forEach((pkg) => {
        const slots = getSlots(pkg, selectedDate);
        const booked = getBookedSlots(pkg, selectedDate);
        // Use backend-calculated available slots
        const available = getAvailableSlots(pkg, selectedDate);

        totalSlots += slots;
        totalBooked += booked;
        totalAvailable += available;

        if (available > 0 && available <= 5) {
          lowAvailabilityCount++;
        }
      });
    });

    return { totalSlots, totalBooked, totalAvailable, lowAvailabilityCount };
  };

  const stats = calculateStatistics();

  // Apply search and filter
  const getFilteredAndSearchedPackages = () => {
    let result = filteredPackages;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result
        .map((trip) => ({
          ...trip,
          packages: trip.packages.filter(
            (pkg) =>
              pkg.name.toLowerCase().includes(query) ||
              trip.tripName.toLowerCase().includes(query)
          ),
        }))
        .filter((trip) => trip.packages.length > 0);
    }

    // Apply availability filter
    if (availabilityFilter !== "all") {
      result = result
        .map((trip) => ({
          ...trip,
          packages: trip.packages.filter((pkg) => {
            // Use backend-calculated available slots
            const available = getAvailableSlots(pkg, selectedDate);

            switch (availabilityFilter) {
              case "low":
                return available > 0 && available <= 5;
              case "medium":
                return available > 5 && available <= 15;
              case "high":
                return available > 15;
              case "full":
                return available === 0;
              default:
                return true;
            }
          }),
        }))
        .filter((trip) => trip.packages.length > 0);
    }

    // If showing low slot update, filter to show only low availability packages (1-5 available slots)
    if (showLowSlotUpdate) {
      result = result
        .map((trip) => ({
          ...trip,
          packages: trip.packages.filter((pkg) => {
            // Use backend-calculated available slots
            const available = getAvailableSlots(pkg, selectedDate);
            // Show only packages with 1-5 available slots
            return available > 0 && available <= 5;
          }),
        }))
        .filter((trip) => trip.packages.length > 0);
    }

    return result;
  };

  const displayedPackages = useMemo(
    () => getFilteredAndSearchedPackages(),
    [packages, searchQuery, availabilityFilter, showLowSlotUpdate, selectedDate]
  );

  const packageCards = useMemo(() => {
    const cards = [];
    displayedPackages.forEach((trip, tripIndex) => {
      trip.packages.forEach((pkg, packageIndex) => {
        cards.push({ trip, pkg, tripIndex, packageIndex, tripName: trip.tripName });
      });
    });
    return cards;
  }, [displayedPackages]);

  const { data: pagedPackageCards, pagination } = useMemo(
    () => paginateClientList(packageCards, currentPage),
    [packageCards, currentPage]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, availabilityFilter, showLowSlotUpdate, selectedDate]);

  const handleSlotUpdate = (tripIndex, packageIndex, value) => {
    const pkg = filteredPackages[tripIndex]?.packages[packageIndex];
    if (!pkg) return;

    const dateStr = formatDateToString(selectedDate);
    const key = `${tripIndex}-${packageIndex}`;

    // Remove any non-numeric characters
    const cleanValue = value.replace(/[^\d]/g, "");

    // Store the input value for display (allows empty string)
    setInputValues((prev) => ({
      ...prev,
      [key]: cleanValue,
    }));

    // If empty, don't update slot state - let user type freely
    if (cleanValue === "") {
      // Clear validation errors when field is empty
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[key];
        return newErrors;
      });
      // Don't update slot state - allow empty input for better UX
      return;
    }

    const slotValue = parseInt(cleanValue) || 0;
    const bookedSlots = getBookedSlots(pkg, selectedDate);

    // Validation: Prevent setting slots below booked count
    if (slotValue < bookedSlots) {
      setValidationErrors((prev) => ({
        ...prev,
        [key]: `Cannot set slots below booked count (${bookedSlots})`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[key];
      return newErrors;
    });

    // Check for low availability warning
    const availableSlots = slotValue - bookedSlots;
    if (availableSlots > 0 && availableSlots <= 5) {
      const slotText = availableSlots === 1 ? "slot" : "slots";
      setLowAvailabilityWarnings((prev) => ({
        ...prev,
        [key]: `Low availability: Only ${availableSlots} ${slotText} remaining`,
      }));
    } else {
      setLowAvailabilityWarnings((prev) => {
        const newWarnings = { ...prev };
        delete newWarnings[key];
        return newWarnings;
      });
    }

    // Store original value if this is the first change
    const currentOriginalValue = originalSlotValues[key];
    if (currentOriginalValue === undefined) {
      const originalSlot = getSlots(pkg, selectedDate);
      setOriginalSlotValues((prev) => ({
        ...prev,
        [key]: originalSlot,
      }));
    }

    // Update local state immediately for better UX
    // Store the actual numeric value for calculations, but allow empty string in input
    const updatedPackages = [...filteredPackages];
    if (!updatedPackages[tripIndex].packages[packageIndex].slots) {
      updatedPackages[tripIndex].packages[packageIndex].slots = {};
    }
    updatedPackages[tripIndex].packages[packageIndex].slots[dateStr] =
      slotValue;

    // Mark as changed
    setChangedPackages((prev) => ({
      ...prev,
      [key]: { packageId: pkg.id, date: dateStr, slot: slotValue },
    }));
  };

  const handleUpdate = async (tripIndex, packageIndex) => {
    const key = `${tripIndex}-${packageIndex}`;
    const change = changedPackages[key];
    if (!change) return;

    try {
      await updateSlotMutation.mutateAsync({
        packageId: change.packageId,
        date: change.date,
        slot: change.slot,
      });

      // Remove from changed packages
      setChangedPackages((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      // Clear original value and input value after successful save
      setOriginalSlotValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      setInputValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      setAvailableInputValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      showToast("Slot updated successfully", "success");
    } catch (error) {
      console.error("Failed to update slot:", error);
      showToast("Failed to update slot. Please try again.", "error");
    }
  };

  // Handle slot blur - auto-save on blur
  const handleSlotBlur = async (tripIndex, packageIndex) => {
    const key = `${tripIndex}-${packageIndex}`;
    const change = changedPackages[key];
    if (!change) {
      // No change to save
      return;
    }

    // Check if value has actually changed from original
    const originalValue = originalSlotValues[key];
    if (originalValue !== undefined && change.slot === originalValue) {
      // Value reverted to original, no need to save
      setChangedPackages((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      setOriginalSlotValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      return;
    }

    // Save the change
    try {
      await handleUpdate(tripIndex, packageIndex);
      // Clear original value and input value after successful save
      setOriginalSlotValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      setInputValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    } catch (error) {
      console.error("Failed to save on blur:", error);
    }
  };

  // Handle available slot update - calculates total slot needed
  const handleAvailableSlotUpdate = (tripIndex, packageIndex, value) => {
    const pkg = filteredPackages[tripIndex]?.packages[packageIndex];
    if (!pkg) return;

    const dateStr = formatDateToString(selectedDate);
    const key = `${tripIndex}-${packageIndex}`;

    // Remove any non-numeric characters
    const cleanValue = value.replace(/[^\d]/g, "");

    // Store the input value for display (allows empty string)
    setAvailableInputValues((prev) => ({
      ...prev,
      [key]: cleanValue,
    }));

    // If empty, don't update - let user type freely
    if (cleanValue === "") {
      return;
    }

    const availableValue = parseInt(cleanValue) || 0;
    const bookedSlots = getBookedSlots(pkg, selectedDate);

    // Calculate total slot needed: total = available + booked
    const totalSlotNeeded = availableValue + bookedSlots;

    // Update the total slot (which will trigger the normal slot update flow)
    handleSlotUpdate(tripIndex, packageIndex, totalSlotNeeded.toString());
  };

  // Handle available slot blur - auto-save
  const handleAvailableSlotBlur = async (tripIndex, packageIndex) => {
    const key = `${tripIndex}-${packageIndex}`;
    const change = changedPackages[key];
    if (!change) {
      // No change to save
      return;
    }

    // Save the change (which updates the total slot)
    try {
      await handleUpdate(tripIndex, packageIndex);
      // Clear available input value after successful save
      setAvailableInputValues((prev) => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
    } catch (error) {
      console.error("Failed to save available slot on blur:", error);
    }
  };

  const changeDate = (offset) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + offset);
    setSelectedDate(date);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }

    return formatListingDate(dateString);
  };

  // Format date range for display
  const formatDateRange = (startDate, endDate) => {
    const startFormatted = formatListingDate(startDate);
    const endFormatted = formatListingDate(endDate);
    return `${startFormatted} - ${endFormatted}`;
  };

  // Get all available date ranges from rates and stop sales
  const getAvailableDateRanges = (pkg) => {
    const ranges = [];

    // Add rate date ranges
    if (
      pkg.rateDateRanges &&
      Array.isArray(pkg.rateDateRanges) &&
      pkg.rateDateRanges.length > 0
    ) {
      pkg.rateDateRanges.forEach((range) => {
        ranges.push({
          start_date: range.start_date,
          end_date: range.end_date,
          type: range.rate_type,
          isStopSale: false,
        });
      });
    }

    // Add stop sale date ranges
    if (
      pkg.stopSaleDateRanges &&
      Array.isArray(pkg.stopSaleDateRanges) &&
      pkg.stopSaleDateRanges.length > 0
    ) {
      pkg.stopSaleDateRanges.forEach((range) => {
        ranges.push({
          start_date: range.start_date,
          end_date: range.end_date,
          type: "stop_sale",
          isStopSale: true,
        });
      });
    }

    // Sort by start date
    ranges.sort((a, b) => new Date(a.start_date) - new Date(b.start_date));

    return ranges;
  };

  // Get all available dates from all packages
  const getAllAvailableDates = () => {
    const availableDates = new Set();

    packages.forEach((trip) => {
      trip.packages.forEach((pkg) => {
        const ranges = getAvailableDateRanges(pkg);
        ranges.forEach((range) => {
          if (!range.isStopSale) {
            const start = new Date(range.start_date);
            const end = new Date(range.end_date);
            const current = new Date(start);

            while (current <= end) {
              availableDates.add(formatDateToString(current));
              current.setDate(current.getDate() + 1);
            }
          }
        });
      });
    });

    return Array.from(availableDates).sort();
  };

  // Calendar component helper functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const availableDates = getAllAvailableDates();

  // Get package count for a specific date
  const getPackageCountForDate = (date) => {
    const dateStr = formatDateToString(date);
    let count = 0;

    packages.forEach((trip) => {
      trip.packages.forEach((pkg) => {
        // Check if this package has a rate for this date
        const ranges = getAvailableDateRanges(pkg);
        const hasRate = ranges.some((range) => {
          if (range.isStopSale) return false;
          return dateStr >= range.start_date && dateStr <= range.end_date;
        });

        if (hasRate) {
          count++;
        }
      });
    });

    return count;
  };

  const isDateAvailable = (date) => {
    const dateStr = formatDateToString(date);
    return availableDates.includes(dateStr);
  };

  const isDateSelected = (date) => {
    return formatDateToString(date) === formatDateToString(selectedDate);
  };

  const isToday = (date) => {
    const today = new Date();
    return formatDateToString(date) === formatDateToString(today);
  };

  const handleCalendarDateClick = (day) => {
    const newDate = new Date(
      calendarMonth.getFullYear(),
      calendarMonth.getMonth(),
      day
    );
    setSelectedDate(newDate);
  };

  const changeCalendarMonth = (offset) => {
    const newMonth = new Date(calendarMonth);
    newMonth.setMonth(newMonth.getMonth() + offset);
    setCalendarMonth(newMonth);
  };

  // Find next available date
  const findNextAvailableDate = () => {
    if (availableDates.length === 0) return null;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = formatDateToString(today);

    // Find the first available date that is today or in the future
    const nextAvailable = availableDates.find((dateStr) => dateStr >= todayStr);

    if (nextAvailable) {
      return new Date(nextAvailable);
    }

    // If no future date, return the first available date
    return new Date(availableDates[0]);
  };

  const handleNextAvailable = () => {
    const nextDate = findNextAvailableDate();
    if (nextDate) {
      setSelectedDate(nextDate);
      setCalendarMonth(new Date(nextDate.getFullYear(), nextDate.getMonth()));
      setShowCalendarSheet(false);
    }
  };

  const handleCloseCalendarSheet = () => {
    setShowCalendarSheet(false);
    // Reset calendar month to selected date's month
    setCalendarMonth(
      new Date(selectedDate.getFullYear(), selectedDate.getMonth())
    );
  };

  // Handle manage date and rate button
  const handleManageDateAndRate = (packageId) => {
    navigate(`/packages/${packageId}/edit`, {
      state: { initialTab: "pricing", returnTo: "/slots" },
    });
  };

  // Bulk operations
  const handleBulkSelect = (tripIndex, packageIndex) => {
    const key = `${tripIndex}-${packageIndex}`;
    setSelectedPackagesForBulk((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  const handleBulkUpdate = async () => {
    if (!bulkSlotValue || bulkSlotValue < 0) {
      showToast("Please enter a valid slot value", "error");
      return;
    }

    const updates = [];
    const dateStr = formatDateToString(selectedDate);
    const errors = [];

    selectedPackagesForBulk.forEach((key) => {
      const [tripIndex, packageIndex] = key.split("-").map(Number);
      const pkg = filteredPackages[tripIndex]?.packages[packageIndex];
      if (!pkg) return;

      const bookedSlots = getBookedSlots(pkg, selectedDate);
      const slotValue = parseInt(bulkSlotValue);

      if (slotValue < bookedSlots) {
        errors.push(`"${pkg.name}" has ${bookedSlots} booked slots`);
        return;
      }

      updates.push({
        packageId: pkg.id,
        date: dateStr,
        slot: slotValue,
      });
    });

    if (errors.length > 0) {
      showToast(
        `${errors.length} package(s) cannot be updated: ${errors.join(", ")}`,
        "warning"
      );
    }

    if (updates.length === 0) {
      showToast("No packages selected or all have validation errors", "error");
      return;
    }

    try {
      await bulkUpdateMutation.mutateAsync(updates);
      showToast(`Successfully updated ${updates.length} package(s)`, "success");
    } catch (error) {
      console.error("Bulk update failed:", error);
      showToast("Failed to update slots. Please try again.", "error");
    }
  };

  // Quick action: Set all visible packages to a value
  const handleSetAllToValue = async () => {
    if (!bulkSlotValue || bulkSlotValue < 0) {
      showToast("Please enter a valid slot value", "error");
      return;
    }

    const updates = [];
    const dateStr = formatDateToString(selectedDate);
    const slotValue = parseInt(bulkSlotValue);

    filteredPackages.forEach((trip, tripIndex) => {
      trip.packages.forEach((pkg, packageIndex) => {
        const bookedSlots = getBookedSlots(pkg, selectedDate);

        if (slotValue < bookedSlots) {
          return; // Skip packages where value < booked
        }

        updates.push({
          packageId: pkg.id,
          date: dateStr,
          slot: slotValue,
        });
      });
    });

    if (updates.length === 0) {
      showToast(
        "No packages can be updated. Some may have more bookings than the entered value.",
        "warning"
      );
      return;
    }

    try {
      await bulkUpdateMutation.mutateAsync(updates);
      setBulkSlotValue("");
      setShowBulkActions(false);
      showToast(`Successfully updated ${updates.length} package(s)`, "success");
    } catch (error) {
      console.error("Set all failed:", error);
      showToast("Failed to update slots. Please try again.", "error");
    }
  };

  if (isLoading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={SUPPLIER_PAGE_META.slots.title}
          subtitle={SUPPLIER_PAGE_META.slots.subtitle}
        />
        <AdminPageCard className="flex h-96 items-center justify-center p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            <p className="text-lg font-medium text-gray-600">Loading packages...</p>
            <p className="mt-2 text-sm text-gray-400">
              Please wait while we fetch your slot data
            </p>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader
          title={SUPPLIER_PAGE_META.slots.title}
          subtitle={SUPPLIER_PAGE_META.slots.subtitle}
        />
        <AdminPageCard className="border-l-4 border-red-400 bg-red-50 p-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-6 w-6 text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-lg font-medium text-red-800">Error loading packages</h3>
              <p className="mt-2 text-sm text-red-700">{error.message}</p>
              <button
                onClick={() => refetch()}
                className="mt-4 rounded-lg bg-red-600 px-4 py-2 font-medium text-white transition-colors hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          </div>
        </AdminPageCard>
      </AdminPageLayout>
    );
  }

  return (
    <AdminPageLayout>
      <AdminPageHeader
        title={SUPPLIER_PAGE_META.slots.title}
        subtitle={SUPPLIER_PAGE_META.slots.subtitle}
        actions={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => navigate("/packages")}
              className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 transition-all hover:bg-gray-50"
            >
              <i className="fi fi-rr-angle-left text-xs" />
              Packages
            </button>
            <button
              onClick={() => setShowCalendarSheet(true)}
              className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 transition-all hover:bg-blue-100"
            >
              <i className="fi fi-rr-calendar text-xs" />
              <span>View Calendar</span>
            </button>
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className={`flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium transition-all ${
                showBulkActions
                  ? "border-purple-600 bg-purple-600 text-white"
                  : "border-purple-200 bg-white text-purple-600 hover:bg-purple-50"
              }`}
            >
              <i className="fi fi-rr-layer-plus text-xs" />
              <span>Bulk Actions</span>
            </button>
          </div>
        }
      />

      <AdminPageCard className="p-4 lg:p-6">
        <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* Date Selection Control */}
          <div className="lg:col-span-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm flex flex-col justify-center">
            <label className="text-[10px] font-semibold text-gray-500 uppercase mb-2 px-1">Active Date Selection</label>
            <div className="flex items-center gap-2">
              <button onClick={() => changeDate(-1)} className="w-9 h-9 flex items-center justify-center rounded-md bg-gray-50 border border-gray-200 hover:bg-white hover:border-gray-300 transition-all text-gray-600">
                <i className="fi fi-rr-arrow-small-left text-lg"></i>
              </button>
              <div className="flex-1 relative">
                <input
                  type="date"
                  value={formatDateToString(selectedDate)}
                  onChange={(e) => setSelectedDate(new Date(e.target.value))}
                  className="w-full h-9 px-3 pl-9 bg-gray-50 text-gray-900 rounded-md text-sm font-medium border border-gray-200 focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary transition-all"
                />
                <i className="fi fi-rr-calendar-day absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
              </div>
              <button onClick={() => changeDate(1)} className="w-9 h-9 flex items-center justify-center rounded-md bg-gray-50 border border-gray-200 hover:bg-white hover:border-gray-300 transition-all text-gray-600">
                <i className="fi fi-rr-arrow-small-right text-lg"></i>
              </button>
            </div>
          </div>

          {/* Statistics Row */}
          <div className="lg:col-span-8 bg-white p-4 rounded-lg border border-gray-200 shadow-sm relative overflow-hidden">
            <div className="grid grid-cols-4 gap-4 relative z-10">
              <div className="text-center">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Stock</p>
                <p className="text-xl font-bold text-blue-600">{stats.totalSlots}</p>
              </div>
              <div className="text-center border-l border-gray-100">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Booked</p>
                <p className="text-xl font-bold text-orange-500">{stats.totalBooked}</p>
              </div>
              <div className="text-center border-l border-gray-100">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Available</p>
                <p className="text-xl font-bold text-emerald-500">{stats.totalAvailable}</p>
              </div>
              <div className="text-center border-l border-gray-100 relative">
                <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Warnings</p>
                <p className="text-xl font-bold text-red-500">{stats.lowAvailabilityCount}</p>
                {stats.lowAvailabilityCount > 0 && (
                  <span className="absolute top-0 right-2 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Global Filters & Bulk Bar */}
        <div className="space-y-4">
          <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm flex flex-col lg:flex-row items-center gap-3">
            <div className="flex-1 relative w-full">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search packages, destinations..."
                className="w-full h-9 px-9 bg-gray-50 border border-gray-200 rounded-md text-sm font-medium focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary transition-all"
              />
              <i className="fi fi-rr-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
            </div>
            <div className="flex items-center gap-2 w-full lg:w-auto">
              <select
                value={availabilityFilter}
                onChange={(e) => setAvailabilityFilter(e.target.value)}
                className="h-9 px-3 bg-gray-50 border border-gray-200 rounded-md text-xs font-medium focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all"
              >
                <option value="all">All Inventory</option>
                <option value="low">Low Support (1-5)</option>
                <option value="medium">Medium (6-15)</option>
                <option value="high">High Velocity (16+)</option>
                <option value="full">Sold Out</option>
              </select>
              <button
                onClick={() => {
                  setShowLowSlotUpdate(!showLowSlotUpdate);
                  if (showLowSlotUpdate) setAvailabilityFilter("all");
                }}
                className={`h-9 px-3 flex items-center justify-center gap-2 rounded-md border text-xs font-medium transition-all ${showLowSlotUpdate
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white text-orange-600 border-orange-100 hover:bg-orange-50'
                  }`}
              >
                <i className="fi fi-rr-tags text-[10px]"></i>
                Critical Only
              </button>
            </div>
          </div>

          {/* Animated Bulk Actions Panel */}
          {showBulkActions && (
            <div className="bg-purple-900 rounded-lg p-4 shadow-lg border border-purple-800 text-white animate-in slide-in-from-top-4 fade-in duration-300">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center">
                    <i className="fi fi-rr-bolt text-lg text-purple-200"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">Bulk Availability Update</p>
                    <p className="text-[10px] font-normal text-purple-300 uppercase">Modify multiple schedules</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 bg-white/5 p-1.5 rounded-lg border border-white/10">
                  <span className="text-xs font-medium px-2">Set slots:</span>
                  <input
                    type="number"
                    value={bulkSlotValue}
                    onChange={(e) => setBulkSlotValue(e.target.value)}
                    min="0"
                    className="w-16 h-8 bg-white text-purple-900 rounded-md text-sm font-bold text-center focus:ring-2 focus:ring-purple-400 outline-none"
                    placeholder="0"
                  />
                  <button
                    onClick={handleSetAllToValue}
                    disabled={bulkUpdateMutation.isPending}
                    className="h-8 px-3 bg-purple-500 hover:bg-purple-400 rounded-md text-xs font-semibold transition-colors disabled:opacity-50"
                  >
                    {bulkUpdateMutation.isPending ? "..." : "Set All"}
                  </button>
                  <div className="w-px h-5 bg-white/10"></div>
                  <button
                    onClick={handleBulkUpdate}
                    disabled={bulkUpdateMutation.isPending || selectedPackagesForBulk.size === 0}
                    className={`h-8 px-3 rounded-md text-xs font-semibold transition-all ${selectedPackagesForBulk.size > 0
                      ? 'bg-white text-purple-900'
                      : 'bg-white/10 text-white/40 cursor-not-allowed'
                      }`}
                  >
                    Update Selected ({selectedPackagesForBulk.size})
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Packages List Render */}
        <div className="space-y-4">
          {!displayedPackages || displayedPackages.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="fi fi-rr-database text-2xl text-gray-300"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900">No Inventory Found</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">
                {searchQuery || availabilityFilter !== "all"
                  ? "We couldn't find any packages matching your active filters. Try resetting the search."
                  : "No trip schedules available for this date. Check your rates and base calendar."}
              </p>
              <button
                onClick={() => { setSearchQuery(""); setAvailabilityFilter("all"); setShowLowSlotUpdate(false); }}
                className="mt-6 px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold text-xs rounded-xl transition-all"
              >
                Clear All Filters
              </button>
            </div>
          ) : (
            <>
            <div className="space-y-4">
            {pagedPackageCards.map(({ trip, pkg, tripIndex, packageIndex, tripName }, cardIndex) => {
              const showTripHeader =
                cardIndex === 0 || pagedPackageCards[cardIndex - 1].tripName !== tripName;
              const totalSlots = getSlots(pkg, selectedDate);
              const bookedSlots = getBookedSlots(pkg, selectedDate);
              const availableSlots = getAvailableSlots(pkg, selectedDate);
              const availableDateRanges = getAvailableDateRanges(pkg);
              const key = `${tripIndex}-${packageIndex}`;
              const isChanged = changedPackages[key];
              const isUpdating = updateSlotMutation.isPending && isChanged;
              const validationError = validationErrors[key];
              const isSelected = selectedPackagesForBulk.has(key);

              return (
                <div key={`${trip.packageGroupId || tripIndex}-${pkg.id}`} className="space-y-3">
                  {showTripHeader && (
                    <div className="flex items-center gap-3 px-1 mt-4 first:mt-0">
                      <span className="text-[10px] font-bold text-gray-500 uppercase">{tripName}</span>
                      <div className="h-px bg-gray-200 flex-1"></div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-3">
                    <div
                      className={`bg-white rounded-lg border transition-all duration-200 overflow-hidden ${isSelected ? "border-purple-300 shadow-md ring-1 ring-purple-100" :
                        validationError ? "border-red-300 shadow-sm" :
                          "border-gray-200 shadow-sm hover:shadow-md hover:border-gray-300"
                        }`}
                    >
                      <div className="p-3 flex flex-col lg:flex-row lg:items-center gap-4">

                        {/* Left: Info & Checkbox */}
                        <div className="flex items-center gap-3 min-w-0 lg:w-[32%] shrink-0">
                          {showBulkActions && (
                            <div
                              onClick={() => handleBulkSelect(tripIndex, packageIndex)}
                              className={`w-4 h-4 rounded border cursor-pointer flex items-center justify-center transition-all ${isSelected ? 'bg-purple-600 border-purple-600' : 'bg-gray-50 border-gray-300 hover:border-purple-300'
                                }`}
                            >
                              {isSelected && <i className="fi fi-rr-check text-[9px] text-white font-bold"></i>}
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <h3 className="font-semibold text-gray-900 text-sm truncate">{pkg.name}</h3>
                              {availableSlots === 0 ? (
                                <span className="px-1.5 py-0.5 bg-red-50 text-[9px] font-bold text-red-600 rounded border border-red-100 uppercase">Sold Out</span>
                              ) : availableSlots <= 5 ? (
                                <span className="px-1.5 py-0.5 bg-orange-50 text-[9px] font-bold text-orange-600 rounded border border-orange-100 uppercase animate-pulse">Low</span>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-1.5">
                              <span className="text-[10px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded border border-gray-100 inline-flex items-center gap-1">
                                <i className="fi fi-rr-calendar text-[9px]"></i>
                                {availableDateRanges.length > 0 ? formatDateRange(availableDateRanges[0].start_date, availableDateRanges[0].end_date) : "No Rates"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Middle: Data Displays */}
                        <div className="flex items-center gap-6 lg:flex-1 justify-between lg:justify-start">
                          {/* Booked Display */}
                          <div className="flex flex-col">
                            <span className="text-[9px] font-bold text-gray-400 uppercase mb-1 leading-none">Booked</span>
                            <div className="px-3 py-1 bg-gray-50 text-gray-700 font-semibold text-sm rounded border border-gray-100 min-w-[50px] text-center">
                              {bookedSlots}
                            </div>
                          </div>

                          {/* Available Input */}
                          <div className="flex flex-col">
                            <span className={`text-[9px] font-bold uppercase mb-1 leading-none ${availableSlots <= 5 ? 'text-orange-500' : 'text-emerald-500'
                              }`}>Available</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={availableInputValues[key] !== undefined ? availableInputValues[key] : (availableSlots || "0")}
                              onChange={(e) => handleAvailableSlotUpdate(tripIndex, packageIndex, e.target.value)}
                              onBlur={() => handleAvailableSlotBlur(tripIndex, packageIndex)}
                              placeholder="0"
                              className={`w-14 h-8 px-2 text-center rounded text-sm font-bold transition-all outline-none border ${availableSlots <= 5 ? 'bg-orange-50 border-orange-200 text-orange-700 focus:bg-white focus:ring-1 focus:ring-orange-200' :
                                'bg-emerald-50 border-emerald-200 text-emerald-700 focus:bg-white focus:ring-1 focus:ring-emerald-200'
                                }`}
                            />
                          </div>

                          {/* Total Input */}
                          <div className="flex flex-col">
                            <span className="text-[9px] font-bold text-gray-400 uppercase mb-1 leading-none">Stock</span>
                            <input
                              type="text"
                              inputMode="numeric"
                              value={inputValues[key] !== undefined ? inputValues[key] : (totalSlots || "0")}
                              onChange={(e) => handleSlotUpdate(tripIndex, packageIndex, e.target.value)}
                              onBlur={() => handleSlotBlur(tripIndex, packageIndex)}
                              placeholder="0"
                              className="w-14 h-8 px-2 bg-gray-50 border border-gray-200 text-gray-900 font-bold text-sm text-center rounded focus:bg-white focus:ring-1 focus:ring-primary focus:border-primary transition-all outline-none"
                            />
                          </div>
                        </div>

                        {/* Right: Actions */}
                        <div className="flex items-center gap-2 shrink-0 justify-end">
                          {isUpdating && (
                            <i className="fi fi-rr-spinner animate-spin text-primary text-xs"></i>
                          )}
                          <button
                            onClick={() => handleManageDateAndRate(pkg.id)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-50 border border-gray-200 text-gray-500 rounded-md hover:bg-white hover:border-gray-300 transition-all"
                            title="Settings"
                          >
                            <i className="fi fi-rr-settings-sliders text-[11px]"></i>
                          </button>
                          <button
                            onClick={() => handleManageDateAndRate(pkg.id)}
                            className="h-8 px-3 flex items-center justify-center text-xs font-medium text-primary hover:text-white bg-gray-50 hover:bg-primary border border-gray-200 hover:border-primary rounded-md transition-all active:scale-95"
                          >
                            Modify Rates
                          </button>
                        </div>

                      </div>
                      {/* Error Feedback */}
                      {validationError && (
                        <div className="px-3 py-1.5 bg-red-600 text-white text-[10px] font-semibold uppercase flex items-center gap-2">
                          <i className="fi fi-rr-exclamation"></i>
                          {validationError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
            <div className="px-4 py-3 border-t border-gray-200 bg-white rounded-2xl">
              <Pagination pagination={pagination} onPageChange={setCurrentPage} />
            </div>
            </>
          )}
        </div>
        </div>
      </AdminPageCard>

      {/* Calendar Sheet */}
      {showCalendarSheet && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
            onClick={handleCloseCalendarSheet}
          ></div>

          {/* Sheet - Slides from right */}
          <div
            className={`fixed top-0 right-0 bottom-0 bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-out w-full max-w-sm flex flex-col ${showCalendarSheet ? "translate-x-0" : "translate-x-full"
              }`}
          >
            {/* Sheet Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800">
                Available Days Calendar
              </h3>
              <button
                onClick={handleCloseCalendarSheet}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Sheet Content */}
            <div className="overflow-y-auto p-4 flex-1">
              <div className="mx-auto">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-3">
                  <button
                    onClick={() => changeCalendarMonth(-1)}
                    className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                    title="Previous Month"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 19l-7-7 7-7"
                      />
                    </svg>
                  </button>
                  <span className="text-sm font-semibold text-gray-800">
                    {calendarMonth.toLocaleDateString("en-US", {
                      month: "long",
                      year: "numeric",
                    })}
                  </span>
                  <button
                    onClick={() => changeCalendarMonth(1)}
                    className="p-1.5 hover:bg-gray-100 rounded transition-colors"
                    title="Next Month"
                  >
                    <svg
                      className="w-4 h-4 text-gray-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>

                {/* Calendar Grid */}
                <div>
                  {/* Weekday headers */}
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                      (day) => (
                        <div
                          key={day}
                          className="text-center text-[10px] font-medium text-gray-500 py-1"
                        >
                          {day}
                        </div>
                      )
                    )}
                  </div>

                  {/* Calendar days */}
                  <div className="grid grid-cols-7 gap-1">
                    {(() => {
                      const { daysInMonth, startingDayOfWeek, year, month } =
                        getDaysInMonth(calendarMonth);
                      const days = [];

                      // Empty cells for days before month starts
                      for (let i = 0; i < startingDayOfWeek; i++) {
                        days.push(
                          <div
                            key={`empty-${i}`}
                            className="aspect-square"
                          ></div>
                        );
                      }

                      // Days of the month
                      for (let day = 1; day <= daysInMonth; day++) {
                        const date = new Date(year, month, day);
                        const dateStr = formatDateToString(date);
                        const available = isDateAvailable(date);
                        const selected = isDateSelected(date);
                        const today = isToday(date);
                        const packageCount = getPackageCountForDate(date);

                        days.push(
                          <button
                            key={day}
                            onClick={() => {
                              handleCalendarDateClick(day);
                              handleCloseCalendarSheet();
                            }}
                            disabled={!available}
                            className={`aspect-square text-xs font-medium rounded transition-all relative ${selected
                              ? "bg-primary-600 text-white ring-2 ring-primary-500/20"
                              : available
                                ? "bg-primary-50 text-primary-700 hover:bg-primary-100 border border-primary-100"
                                : "bg-gray-50 text-gray-300 cursor-not-allowed"
                              } ${today && !selected ? "ring-1 ring-gray-400" : ""
                              }`}
                            title={
                              available
                                ? `${packageCount} package${packageCount !== 1 ? "s" : ""
                                } available - Click to select ${dateStr}`
                                : "No rates available"
                            }
                          >
                            <div className="flex flex-col items-center justify-center h-full">
                              <span>{day}</span>
                              {available && packageCount > 0 && (
                                <span
                                  className={`text-[9px] font-semibold mt-0.5 ${selected ? "text-white" : "text-primary-600"
                                    }`}
                                >
                                  {packageCount}
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      }

                      return days;
                    })()}
                  </div>

                  {/* Legend */}
                  <div className="flex items-center justify-center gap-4 mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded bg-primary-50 border border-primary-100"></div>
                      <span className="text-[10px] text-gray-600">
                        Available
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-3 rounded bg-gray-50 border border-gray-200"></div>
                      <span className="text-[10px] text-gray-600">
                        Not Available
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {showToster && (
        <Toster
          message={toasterMessage}
          type={toasterType}
          duration={3000}
          onClose={() => setShowToster(false)}
        />
      )}
    </AdminPageLayout>
  );
};

export default Slots;
